import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { adocbe, adocbf, adocbfbf } from '@cyberdefense-im2/carontesdkweb';
import { from, lastValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { mapCharonParams } from '../../core/handle-params';
import { isCharonOrBFFError, isCryptoError } from './defineErrorType';

export interface CharonData {
  charonId: string;
  isEncrypted: boolean;
}

const charonData: CharonData = {
  charonId: '',
  isEncrypted: false
};

export const IS_CHARON_REQUEST = new HttpContextToken<CharonData>(() => charonData);
export const CHARON_PARAMS = new HttpContextToken<HttpParams>(() => new HttpParams());

@Injectable({
  providedIn: 'root'
})
export class CharonHttpInterceptor implements HttpInterceptor {

  /**
  * Identifica e trata uma determinada requisição HTTP
  *
  * Identifies and handles a given HTTP request
  * 
  * @param request requisição HTTP a ser tratada
  * @param next próximo interceptor
  */
  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const context = request.context.get(IS_CHARON_REQUEST);
    if (context.isEncrypted) {
      return from(this.handleCharonRequest(request, next));
    }

    return next.handle(request);
  }

  /**
  * Trata a requisição HTTP interceptada
  *
  * Handles intercepted HTTP request 
  * 
  * @param request requisição HTTP a ser tratada
  * @param next próximo interceptor
  */
  private async handleCharonRequest(request: HttpRequest<unknown>, next: HttpHandler): Promise<HttpEvent<unknown> | any> {
    let headers = request.headers;

    const charonParams = request.context.get(CHARON_PARAMS);
    const context = request.context.get(IS_CHARON_REQUEST);

    if (charonParams['updates']) {
      const parse = charonParams['updates'];
      headers = headers.append('X-Charon-Params', (await adocbe(JSON.stringify(mapCharonParams(parse)), context.charonId) as string));
    }

    const body = await this.encryptBody(request);

    request = request.clone({
      headers,
      body
    });

    return this.decryptBody(request, next)
      .catch(async (e) => {
        let filteredError: unknown;
        const typeOfError = isCharonOrBFFError(e);

        if (isCryptoError(typeOfError)) {
          filteredError = JSON.parse(await adocbf(typeOfError, context.charonId));
        } else if (typeOfError && typeOfError.includes('{')) {
          filteredError = JSON.parse(typeOfError);
        }

        throw new HttpErrorResponse({
          ...e,
          error: filteredError
        });
      });
  }

  /**
  * Descriptografa o body da requisição
  *
  * Decrypt the request body
  * 
  * @param request requisição HTTP a ser tratada
  * @param next próximo interceptor
  */
  private async decryptBody(request: HttpRequest<unknown>, next: HttpHandler): Promise<HttpEvent<any> | unknown> {
    return lastValueFrom(next.handle(request)
      .pipe(
        map(async (event: HttpEvent<any>) => {
          if (!(event instanceof HttpResponse) || !event.ok) {
            return event;
          }


          let body: string | undefined;
          const isEncodingType = event.headers.get('X-Content-Encoding') === 'application/vnd-encode-4';
          const isContentType = event.headers.get('Content-Type')?.includes('application/json') || event.headers.get('Content-Type') === 'text/plain';
          const context = request.context.get(IS_CHARON_REQUEST);

          if (isEncodingType && isContentType) {
            body = typeof event.body === 'string' ? await this.decryptBodyType(event.body, request.responseType, context.charonId) : event.body;
          } else {
            body = event.body;
          }

          const eventHandle = event.clone({
            body
          });

          return eventHandle;

        })));
  }

  async decryptBodyType(body: string, type: string, charonId: string): Promise<string | PromiseLike<string>> {
    const types: any = {
      "arraybuffer": async (b: string, c: string) => await adocbfbf(b, c),
      "blob": async (b: string, c: string) => await adocbfbf(b, c),
      "text": async (b: string, c: string) => JSON.parse(await adocbf(b, c)),
      "json": async (b: string, c: string) => JSON.parse(await adocbf(b, c))
    };
    return types[type](body, charonId);
  }

  /**
    * Criptografa o body da requisição
    *
    * Encrypt request body
    * 
    * @param request requisição HTTP a ser tratada
    */
  private async encryptBody(request: HttpRequest<unknown>) {
    let body;
    const methods = ['POST', 'PUT', 'PATCH'];

    const isSomeMethod = methods!.some((element) => request.method.toUpperCase() === element);

    const context = request.context.get(IS_CHARON_REQUEST);

    if (isSomeMethod) {
      if (request.body) {
        const serialize = (request as HttpRequest<any>).serializeBody();
        if (typeof serialize === 'string') {
          body = `${(await adocbe(serialize, context.charonId))}`;
        }
      }
    }

    return body;
  }

}
